import { isEqual } from 'lodash';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { EditorContext, ModalContext, PluginsEventsContext, RicosContext } from 'ricos-context';
import type { TextDirection } from 'ricos-types';
import { FooterActions, SidePanelLayout } from 'wix-rich-content-ui-components';

import { CollapsibleListSettings } from '../components/modal-components/CollapsibleListSettings';
import { collapsibleModals } from '../consts';
import { MODAL_ROOT } from '../dataHooks';
import type { CollapsibleListAttributes } from '../tiptap/utils';
import type { CollapsibleListComponentData, ListDirection } from '../types';
import { COLLAPSIBLE_LIST_TYPE } from '../types';

export type SettingsProps = {
  nodeId: string;
  isQapageDataEnabled: boolean;
};

export type SettingsState = {
  initialExpanded: { type: 'ALL' } | { type: 'FIRST' | 'NONE'; expandOnlyOne: boolean };
  direction: ListDirection;
  isQapageData: boolean;
};

const CollapsibleListSettingsModal: FC<SettingsProps> = ({ nodeId, isQapageDataEnabled }) => {
  const { t, languageDir } = useContext(RicosContext);
  const modalService = useContext(ModalContext) || {};
  const {
    adapter: { tiptapEditor },
    getEditorCommands,
  } = useContext(EditorContext);
  const pluginsEvents = useContext(PluginsEventsContext);

  const [initialComponentData] = useState<CollapsibleListComponentData>(() =>
    getEditorCommands().getBlockComponentData(nodeId)
  );

  const fromComponentData = (componentData: CollapsibleListComponentData): SettingsState => {
    const textDirectionToList = (textDirection: TextDirection): ListDirection => {
      if (textDirection === 'rtl') {
        return 'RTL';
      }
      return 'LTR';
    };

    return {
      initialExpanded:
        componentData.initialExpandedItems === 'ALL'
          ? {
              type: 'ALL',
            }
          : {
              type: componentData.initialExpandedItems,
              expandOnlyOne: componentData.expandOnlyOne,
            },
      direction: componentData?.direction || textDirectionToList(languageDir),
      isQapageData: !!componentData.isQapageData,
    };
  };

  const toComponentData = (
    settings: SettingsState,
    originalComponentData: CollapsibleListComponentData
  ): CollapsibleListComponentData => ({
    containerData: originalComponentData.containerData,
    expandOnlyOne:
      settings.initialExpanded.type === 'ALL' ? false : settings.initialExpanded.expandOnlyOne,
    initialExpandedItems: settings.initialExpanded.type,
    direction: settings.direction,
    isQapageData: settings.isQapageData,
  });

  const toListAttributes = (value: SettingsState): CollapsibleListAttributes => ({
    expandOnlyOne:
      value.initialExpanded.type === 'ALL' ? false : value.initialExpanded.expandOnlyOne,
    initialExpandedItems: value.initialExpanded.type,
  });

  const [settings, setSettings] = useState<SettingsState>(() =>
    fromComponentData(initialComponentData)
  );

  const pluginEvents = {
    onChangePluginSettings: data => pluginsEvents.publishPluginChangeSettings({ ...data, nodeId }),
  };

  const setCollapsibleListComponentData = (data: CollapsibleListComponentData) => {
    getEditorCommands().setBlock(nodeId, COLLAPSIBLE_LIST_TYPE, data);
  };

  const handleChange = (value: SettingsState) => {
    setSettings(value);
    setCollapsibleListComponentData(toComponentData(value, initialComponentData));
    tiptapEditor.chain().focus().setCollapsibleListAtributes(nodeId, toListAttributes(value));
  };

  const closeModal = () => {
    modalService.closeModal(collapsibleModals.settings);
  };

  const handleCancel = () => {
    const initialSettings = fromComponentData(initialComponentData);

    if (!isEqual(initialSettings, settings)) {
      handleChange(initialSettings);
    }

    closeModal();
  };

  return settings ? (
    <SidePanelLayout
      onCloseButtonClick={handleCancel}
      dataHook={MODAL_ROOT}
      header={{ title: t('CollapsibleList_CollapsibleListSettings_Common_Header') }}
      content={{
        children: (
          <CollapsibleListSettings
            value={settings}
            onChange={handleChange}
            pluginEvents={pluginEvents}
            isQapageDataEnabled={isQapageDataEnabled}
          />
        ),
      }}
      footer={{ children: <FooterActions onCancel={handleCancel} onSave={closeModal} /> }}
    />
  ) : null;
};

export default CollapsibleListSettingsModal;
